/**
 * config.js — Centralized configuration constants
 *
 * Single source of truth for R2 URLs, API endpoints, and network timeouts.
 * Import from here instead of hardcoding values in individual modules.
 */

import { RESOLVED_OVERTURE_RELEASE } from './overture-release.generated.js'

// ── R2 Public Buckets ──────────────────────────────────────────────
// Bucket 1: geoBoundaries, admin data, lessons, PMTiles (earthvoice-simplified).
// Custom R2 domain (Phase 0 of v3): replaces the rate-limited pub-*.r2.dev host.
export const R2_BASE_URL = 'https://assets.earthvoice.education'

// Bucket 2: DuckDB Parquet data (earthvoicev2) — set via env for flexibility
// (production: https://data.earthvoice.education, see .env.production)
export const R2_PARQUET_URL = import.meta.env.VITE_R2_PUBLIC_URL || ''

// ── Derived R2 Paths ───────────────────────────────────────────────
export const R2_GEOBOUNDARIES_BASE = `${R2_BASE_URL}/geoboundaries_data`
// PMTiles base. Override with VITE_PMTILES_BASE to point at a test prefix
// (e.g. the Overture+geoBoundaries-HP pilot at
// https://data.earthvoice.education/overture_divisions/pmtiles). Unset → production
// geoBoundaries tiles, so default behaviour is unchanged.
export const R2_PMTILES_BASE =
  import.meta.env.VITE_PMTILES_BASE || `${R2_BASE_URL}/geoboundaries_data/pmtiles`

// ── Overture Maps PMTiles (public "extras" bucket) ─────────────────
// Overture retired the old -beta tiles bucket (every theme 403s /
// AllAccessDisabled — audit F-1, 2026-07-28). Ready-made PMTiles now live at
//   overturemaps-extras-us-west-2/tiles/<RELEASE>/<THEME>.pmtiles
// (themes: base, places, buildings, transportation). Overture labels these
// "X-ray" inspection tiles, not a production cartographic basemap. Overture
// retains only the two newest releases and rotates them, so the release is
// RESOLVED AT BUILD TIME from the bucket listing (scripts/resolve-overture-release.mjs,
// run by the `prebuild` hook) into overture-release.generated.js. Precedence:
// VITE_OVERTURE_RELEASE override -> resolved value -> hardcoded fallback below.
export const OVERTURE_RELEASE =
  import.meta.env.VITE_OVERTURE_RELEASE || RESOLVED_OVERTURE_RELEASE || '2026-07-22.0'
export const OVERTURE_PMTILES_BASE =
  import.meta.env.VITE_OVERTURE_PMTILES_BASE ||
  `https://overturemaps-extras-us-west-2.s3.us-west-2.amazonaws.com/tiles/${OVERTURE_RELEASE}`

export const R2_LESSONS_BASE = `${R2_BASE_URL}/lessons`

// ── API ────────────────────────────────────────────────────────────
export const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// ── Network Timeouts (ms) ──────────────────────────────────────────
export const TIMEOUT_FETCH_DEFAULT = 8000 // Standard R2/API fetches
export const TIMEOUT_FETCH_FAST = 3000 // Local-first fetches (fallback only)
export const TIMEOUT_DUCKDB_QUERY = 15000 // DuckDB WASM query execution
export const TIMEOUT_LESSON_FETCH = 15000 // Lesson JSON fetch from R2

// ── "The" Countries — Semantic Rulebook ───────────────────────────
// Countries where "the" is standard or very common in everyday English.
// Used to normalize country names across the parsing pipeline.
//
// Category A: "The" is mandatory or near-mandatory in speech
//   The Bahamas, The Gambia, The Netherlands, The Philippines,
//   The Maldives, The Seychelles, The Comoros, The Solomon Islands,
//   The Marshall Islands, The Central African Republic, The Czech Republic,
//   The Dominican Republic, The United States, The United Kingdom,
//   The United Arab Emirates, The Federated States of Micronesia,
//   The Congo (both), The Democratic Republic of the Congo
//
// Category B: Historical/borderline — "the" is old-fashioned but still heard
//   The Sudan, The Lebanon, The Yemen, The Ukraine, The Argentine
//
// Map: lowercase "the X" → normalized name (matching alias dictionary)
export const THE_COUNTRIES = new Map([
  // Category A — mandatory/near-mandatory "the"
  ['the bahamas', 'bahamas'],
  ['the gambia', 'gambia'],
  ['the netherlands', 'netherlands'],
  ['the philippines', 'philippines'],
  ['the maldives', 'maldives'],
  ['the seychelles', 'seychelles'],
  ['the comoros', 'comoros'],
  ['the solomon islands', 'solomon islands'],
  ['the marshall islands', 'marshall islands'],
  ['the central african republic', 'central african republic'],
  ['the czech republic', 'czech republic'],
  ['the dominican republic', 'dominican republic'],
  ['the united states', 'united states'],
  ['the united states of america', 'united states of america'],
  ['the united kingdom', 'united kingdom'],
  ['the united arab emirates', 'united arab emirates'],
  ['the federated states of micronesia', 'federated states of micronesia'],
  ['the congo', 'congo'],
  ['the democratic republic of the congo', 'democratic republic of the congo'],
  ['the drc', 'drc'],
  ['the dr congo', 'dr congo'],
  ['the uae', 'uae'],
  ['the uk', 'uk'],
  ['the us', 'us'],
  ['the usa', 'usa'],
  // Category B — historical/borderline (still heard in speech)
  ['the sudan', 'sudan'],
  ['the lebanon', 'lebanon'],
  ['the yemen', 'yemen'],
  ['the ukraine', 'ukraine'],
  ['the argentine', 'argentina'],
])

/**
 * Normalize a country name by handling "the" prefix intelligently.
 *
 * Uses the THE_COUNTRIES rulebook to strip "the" only when appropriate,
 * preserving internal "the" (e.g. "Democratic Republic of the Congo").
 *
 * @param {string} name - Raw country name from user input
 * @returns {string} Normalized name suitable for alias lookup
 */
export function normalizeCountryName(name) {
  if (!name) return ''
  const trimmed = name.trim()
  const lower = trimmed.toLowerCase()

  // Check if this is a known "the" country
  const mapped = THE_COUNTRIES.get(lower)
  if (mapped) return mapped

  // Strip leading "the" for unknown names (generic fallback)
  return lower.replace(/^the\s+/, '').trim()
}

// ── Raster Pipeline ───────────────────────────────────────────────
export const RASTER_CATALOG_URL = `${R2_BASE_URL}/raster/raster_catalog.json`

// ── Desert Countries (ISO3) ────────────────────────────────────────
// Countries where yellow outlines don't contrast well against sandy
// satellite imagery — these use pink outlines instead.
export const DESERT_COUNTRIES = new Set([
  'SAU',
  'EGY',
  'DZA',
  'LBY',
  'IRQ',
  'JOR',
  'OMN',
  'ARE',
  'YEM',
  'QAT',
  'BHR',
  'KWT',
  'TUN',
  'MAR',
  'MRT',
  'MLI',
  'NER',
  'TCD',
  'SDN',
  'SSD',
  'ERI',
  'DJI',
  'SOM',
  'NAM',
  'BWA',
  'ESH',
  'SYR',
  'IRN',
  'AFG',
  'PAK',
  'TKM',
  'UZB',
])

/**
 * Check if a GeoJSON FeatureCollection represents a desert country.
 * Looks at the first feature's ISO code (various property name formats).
 */
export function isDesertCountry(geojson) {
  const f = geojson?.features?.[0]
  if (!f) return false
  const iso =
    f.properties?.iso3 ||
    f.properties?.parent_iso3 ||
    f.properties?.shapeGroup ||
    f.properties?.ISO_A3 ||
    f.properties?.iso_a3 ||
    ''
  return DESERT_COUNTRIES.has(iso.toUpperCase())
}
